import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { addEpisodeToSeason as addEpisodeToAnimeSeason } from '../services/animeService';
import { addEpisodeToSeason as addEpisodeToSeriesSeason } from '../services/seriesService';
import axios from 'axios';

const AddEpisodeModal = ({ animeId, seriesId, seasons, onClose, onSuccess }) => {
  const animes = useSelector((state) => state.animes.items);
  const series = useSelector((state) => state.series?.items || []);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSeasonNumber, setSelectedSeasonNumber] = useState('');
  const [contentType, setContentType] = useState('');
  const [contentName, setContentName] = useState('');
  const [availableSeasons, setAvailableSeasons] = useState([]);
  
  // Determinar si es anime o serie
  useEffect(() => {
    console.log("AddEpisodeModal - Props recibidos:", { animeId, seriesId, seasons });
    console.log("Temporadas recibidas:", seasons);
    
    if (animeId) {
      const selectedAnime = animes.find(anime => anime.id === parseInt(animeId));
      console.log("Anime seleccionado:", selectedAnime);
      setContentType('anime');
      setContentName(selectedAnime?.name || '');
      setAvailableSeasons(seasons || []);
      console.log("Temporadas de anime establecidas:", seasons);
    } else if (seriesId) {
      const selectedSeries = series.find(s => s.id === parseInt(seriesId));
      console.log("Serie seleccionada:", selectedSeries);
      setContentType('series');
      setContentName(selectedSeries?.name || '');
      
      // Cargar temporadas de la serie si no se proporcionaron
      if (!seasons || seasons.length === 0) {
        console.log("No se proporcionaron temporadas para la serie, cargando desde API...");
        loadSeriesSeasons(seriesId);
      } else {
        console.log("Temporadas de serie establecidas:", seasons);
        setAvailableSeasons(seasons);
      }
    }
  }, [animeId, seriesId, animes, series, seasons]);
  
  const loadSeriesSeasons = async (seriesId) => {
    try {
      console.log("Cargando temporadas para serie ID:", seriesId);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/v1/seasons/content/series/${seriesId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        }
      );
      
      console.log("Respuesta de API de temporadas:", response);
      
      if (response.data && response.data.data) {
        console.log("Temporadas obtenidas de API:", response.data.data);
        setAvailableSeasons(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        console.log("Temporadas obtenidas (formato array):", response.data);
        setAvailableSeasons(response.data);
      } else {
        console.error("Formato de respuesta no reconocido:", response.data);
      }
    } catch (error) {
      console.error('Error al cargar temporadas:', error);
      if (error.response) {
        console.error('Detalles de error:', error.response.status, error.response.data);
      }
      setError('No se pudieron cargar las temporadas');
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    episodeNumber: '',
    runtime: '',
    description: '',
    videoUrl: '',
    posterUrl: '',
    subtitleUrl: '',
    fileSize: '',
    mimeType: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSeasonChange = (e) => {
    console.log("Temporada seleccionada:", e.target.value);
    setSelectedSeasonNumber(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if ((!animeId && !seriesId) || !selectedSeasonNumber) {
      setError(`Debes seleccionar un ${contentType === 'anime' ? 'anime' : 'serie'} y una temporada`);
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      // Preparar datos según el formato esperado por el backend
      const episodeData = {
        name: formData.name,
        episodeNumber: parseInt(formData.episodeNumber, 10),
        description: formData.description || null,
        runtime: parseInt(formData.runtime, 10) || null,
        videoUrl: formData.videoUrl,
        poster: formData.posterUrl || null,
        srtUrl: formData.subtitleUrl || null,
        fileSize: formData.fileSize ? parseInt(formData.fileSize, 10) : null,
        mimeType: formData.mimeType || null
      };
      
      let response;
      
      if (contentType === 'anime') {
        // Llamar al servicio para agregar episodio a anime
        console.log("Agregando episodio a anime:", animeId, "temporada:", selectedSeasonNumber);
        response = await addEpisodeToAnimeSeason(
          animeId,
          selectedSeasonNumber,
          episodeData
        );
      } else {
        // Llamar al servicio para agregar episodio a serie
        console.log("Agregando episodio a serie:", seriesId, "temporada:", selectedSeasonNumber);
        
        // Verificar si estamos usando la función del servicio o llamando directamente a la API
        if (typeof addEpisodeToSeriesSeason === 'function') {
          response = await addEpisodeToSeriesSeason(
            seriesId,
            selectedSeasonNumber,
            episodeData
          );
        } else {
          // Llamada directa a la API como fallback
          const token = localStorage.getItem("token");
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
          
          // Encontrar el ID de la temporada seleccionada
          const selectedSeason = availableSeasons.find(
            season => season.seasonNumber.toString() === selectedSeasonNumber.toString()
          );
          
          console.log("Temporada seleccionada:", selectedSeason);
          
          if (!selectedSeason) {
            throw new Error('Temporada no encontrada');
          }
          
          const apiResponse = await axios.post(
            `${API_URL}/api/v1/episodes`,
            {
              ...episodeData,
              seasonId: selectedSeason.id
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          response = apiResponse.data;
        }
      }
      
      console.log("Respuesta de creación de episodio:", response);
      
      if (response.success) {
        // Notificar éxito
        onSuccess();
        onClose();
      } else {
        setError(response.message || `Error al agregar episodio a ${contentType === 'anime' ? 'anime' : 'serie'}`);
      }
    } catch (err) {
      console.error(`Error al agregar episodio a ${contentType}:`, err);
      setError(err.response?.data?.message || `Error al agregar episodio a ${contentType === 'anime' ? 'anime' : 'serie'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Debug render
  console.log("Renderizando AddEpisodeModal con:", {
    contentType,
    contentName,
    availableSeasons: availableSeasons?.length || 0,
    selectedSeasonNumber
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Añadir Episodio</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">
              {contentType === 'anime' ? 'Anime' : 'Serie'}
            </label>
            <input 
              type="text"
              value={contentName || `Selecciona un ${contentType === 'anime' ? 'anime' : 'serie'} primero`}
              disabled
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="seasonNumber" className="block text-sm font-medium text-gray-300">
              Temporada
            </label>
            {availableSeasons && availableSeasons.length > 0 ? (
              <select
                id="seasonNumber"
                name="seasonNumber"
                value={selectedSeasonNumber}
                onChange={handleSeasonChange}
                required
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              >
                <option value="">Selecciona una temporada</option>
                {availableSeasons.map(season => (
                  <option key={season.id} value={season.seasonNumber.toString()}>
                    {season.title || `Temporada ${season.seasonNumber}`}
                  </option>
                ))}
              </select>
            ) : (
              <div className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white">
                {availableSeasons ? 'Cargando temporadas...' : 'No hay temporadas disponibles'}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="episodeNumber" className="block text-sm font-medium text-gray-300">
                Número de Episodio *
              </label>
              <input
                type="number"
                id="episodeNumber"
                name="episodeNumber"
                value={formData.episodeNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="runtime" className="block text-sm font-medium text-gray-300">
                Duración (minutos)
              </label>
              <input
                type="number"
                id="runtime"
                name="runtime"
                value={formData.runtime}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300">
              Nombre del Episodio *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-300">
              URL de Video *
            </label>
            <input
              type="text"
              id="videoUrl"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleChange}
              required
              placeholder="https://ejemplo.com/video.mp4"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="posterUrl" className="block text-sm font-medium text-gray-300">
              URL del Póster
            </label>
            <input
              type="text"
              id="posterUrl"
              name="posterUrl"
              value={formData.posterUrl}
              onChange={handleChange}
              placeholder="https://ejemplo.com/poster.jpg"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="subtitleUrl" className="block text-sm font-medium text-gray-300">
              URL de Subtítulo
            </label>
            <input
              type="text"
              id="subtitleUrl"
              name="subtitleUrl"
              value={formData.subtitleUrl}
              onChange={handleChange}
              placeholder="https://ejemplo.com/subtitulos.vtt"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="fileSize" className="block text-sm font-medium text-gray-300">
                Tamaño del Archivo (KB)
              </label>
              <input
                type="number"
                id="fileSize"
                name="fileSize"
                value={formData.fileSize}
                onChange={handleChange}
                placeholder="1024"
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>

            <div>
              <label htmlFor="mimeType" className="block text-sm font-medium text-gray-300">
                Tipo MIME
              </label>
              <input
                type="text"
                id="mimeType"
                name="mimeType"
                value={formData.mimeType}
                onChange={handleChange}
                placeholder="video/mp4"
                className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 border border-transparent rounded-md font-medium text-white hover:bg-green-700"
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : 'Añadir Episodio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEpisodeModal;
